import { memo, useCallback } from "react";
import { Icon } from "@iconify/react";
import { Colors } from "../../utils/colors";
import "../../css/Onboarding.css";
import { ClaireSawaCarousel } from "../../assets/LandingPage/Carousel/BottomCarousel/bottomCarouselIndex";
import IntegrateSocialsFlowRecipe from "../../assets/LandingPage/MainPage/IntegrateSocialsFlowRecipe.png";
import { FoldHeaderText } from "./SubComponents/FoldHeaderText";
import RectangleButton from "../Buttons/RectangleButton";
import { MARKIT_DOCS } from "@markit/common.utils";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import LabelButton from "../Buttons/LabelButton";

type IntegrateSocialsFoldProps = {
  isDesktopSize: boolean;
};

const inboxRowStyle = (highlighted: boolean) => ({
  gap: 10,
  padding: "10px 12px",
  borderRadius: 14,
  border: `1px solid ${highlighted ? "rgba(0,122,255,0.5)" : "rgba(255,255,255,0.06)"}`,
  backgroundColor: highlighted
    ? "rgba(0,122,255,0.12)"
    : "rgba(255,255,255,0.03)",
  boxShadow: highlighted ? "0 0 24px 0 rgba(0,122,255,0.35)" : "none",
  opacity: highlighted ? 1 : 0.5,
  width: highlighted ? "100%" : "92%",
  boxSizing: "border-box" as const,
});

const avatarInitial = (letter: string, gradient: string, size: number = 30) => (
  <div
    className="ColumnCentering"
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      backgroundImage: gradient,
      flexShrink: 0,
    }}
  >
    <span style={{ color: Colors.WHITE, fontSize: 12, fontWeight: 500 }}>
      {letter}
    </span>
  </div>
);

const pingDot = () => (
  <div style={{ position: "relative", width: 12, height: 12 }}>
    <div
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        backgroundColor: Colors.BLUE5,
        animation:
          "onboarding-dot-ping 1.8s cubic-bezier(0, 0, 0.2, 1) infinite",
      }}
    />
    <div
      style={{
        position: "relative",
        width: 12,
        height: 12,
        borderRadius: "50%",
        backgroundColor: Colors.BLUE5,
        boxShadow: "0 0 18px 6px rgba(0,122,255,0.6)",
      }}
    />
  </div>
);

const connector = (label: string) => (
  <div
    className="ColumnCentering"
    style={{ flex: 1, minWidth: 24, alignSelf: "center" }}
  >
    <div style={{ position: "relative", width: "100%", height: 1 }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,122,255,0.35)",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {pingDot()}
      </div>
    </div>
    <span
      style={{
        marginTop: 10,
        color: Colors.BLUE5,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 1.2,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  </div>
);

const verticalConnector = (label: string) => (
  <div
    style={{
      position: "relative",
      height: 64,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: "50%",
        width: 1,
        transform: "translateX(-50%)",
        backgroundColor: "rgba(0,122,255,0.35)",
      }}
    />
    {pingDot()}
    {label ? (
      <span
        style={{
          position: "absolute",
          left: "calc(50% + 16px)",
          top: "50%",
          transform: "translateY(-50%)",
          color: Colors.BLUE5,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    ) : null}
  </div>
);

const IntegrateSocialsFold = (props: IntegrateSocialsFoldProps) => {
  const { isDesktopSize } = props;

  const styles = {
    headerLabel: {
      fontSize: isDesktopSize ? 16 : 14,
      fontWeight: 500,
    },
    button: {
      padding: "12px 16px",
      borderRadius: 10,
      width: "fit-content",
    },
    flowHeaderText: {
      color: Colors.BLUE5,
      fontSize: 14,
      fontWeight: 600,
      letterSpacing: 1.2,
    },
  };

  const learnMoreOnPress = useCallback(() => {
    window.open(MARKIT_DOCS + "/autodmoverview", "_blank");
  }, []);

  return (
    <div
      className={"ColumnNormal"}
      style={{ gap: isDesktopSize ? 70 : 40, alignItems: "flex-start" }}
    >
      <div
        className="ColumnNormal"
        style={{ gap: 24, width: isDesktopSize ? 490 : "100%", flexShrink: 0 }}
      >
        <div className="ColumnNormal" style={{ gap: 10 }}>
          <div className="AlignedRow" style={{ gap: 7 }}>
            <Icon
              icon="mdi:instagram"
              height={14}
              style={{ color: Colors.PURPLE7 }}
            />
            <span style={{ ...styles.headerLabel, color: Colors.PURPLE7 }}>
              Integrate with Socials
            </span>
          </div>
          <FoldHeaderText isDesktopSize={isDesktopSize}>
            Grow text list from Instagram
          </FoldHeaderText>
        </div>
        <span className="landingPageHeaderSubtext">
          No more manual DMs. Auto-DM automatically turns comments, DMs, and
          story replies into text subscribers you own instead of rent, without
          sending anyone to a separate site.
        </span>
        <div className="AlignedRow" style={{ gap: 24 }}>
          <RectangleButton
            buttonLabel="Learn More"
            onPress={learnMoreOnPress}
            altColor={Colors.WHITE}
            altTextColor={Colors.BLACK}
            iconRight={
              <Icon
                icon="ion:chevron-forward"
                height={16}
                style={{ color: Colors.BLACK }}
              />
            }
            containerStyles={styles.button}
          />
          <div className="AlignedColumn" style={{ gap: 2 }}>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <Icon icon="logos:meta-icon" height={26} />
              <span
                style={{ color: Colors.WHITE, fontSize: 26, fontWeight: 600 }}
              >
                Meta
              </span>
            </div>
            <span className="bodyMinyMedium" style={{ color: Colors.WHITE }}>
              Tech Provider
            </span>
          </div>
        </div>
      </div>
      {/* Flow */}
      <div className="slide-up" style={{ position: "relative", width: "100%" }}>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isDesktopSize ? "90%" : "min(90%, 340px)",
            height: "70%",
            backgroundImage:
              "radial-gradient(ellipse at center, rgba(0,122,255,0.28) 0%, rgba(0,122,255,0.12) 45%, rgba(0,122,255,0) 75%)",
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <div
          className={isDesktopSize ? "AlignedRow" : "ColumnCentering"}
          style={{
            position: "relative",
            gap: isDesktopSize ? 4 : 14,
            width: "100%",
            alignItems: isDesktopSize ? "flex-start" : "center",
          }}
        >
          {/* Stage 1 */}
          <div
            className="ColumnNormal"
            style={{
              gap: 14,
              width: isDesktopSize ? 310 : "100%",
              maxWidth: isDesktopSize ? undefined : 332,
              alignItems: isDesktopSize ? "flex-start" : "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                ...styles.flowHeaderText,
                textAlign: isDesktopSize ? "left" : "center",
              }}
            >
              1 — THEY COMMENT ON SOCIAL MEDIA
            </span>
            <div
              className="ColumnNormal"
              style={{
                width: isDesktopSize ? 260 : "100%",
                gap: 12,
                padding: 14,
                borderRadius: 20,
                border: "1px solid rgba(255,255,255,0.24)",
                backgroundImage: "linear-gradient(180deg, #1b1b1e, #0e0e11)",
                boxShadow: "0 12px 20px rgba(0,0,0,0.6)",
              }}
            >
              <div className="AlignedRow" style={{ gap: 7 }}>
                <img
                  src={ClaireSawaCarousel}
                  alt="Claire Sawa"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <span className="bodyMedium" style={{ color: Colors.WHITE }}>
                  @clairesawa
                </span>
                <Icon
                  icon="mdi:check-decagram"
                  height={13}
                  style={{ color: Colors.BLUE5 }}
                />
                <div style={{ flex: 1 }} />
                <span style={{ color: Colors.GRAY1, fontSize: 11 }}>2m</span>
              </div>
              <span
                className="smallBodyPlaceholder"
                style={{ lineHeight: "18px" }}
              >
                comment &quot;coffee&quot; and I&apos;ll send you the recipe!!
              </span>
              <HorizontalDivider
                altMargin={0}
                altColor={Colors.BLACK1}
                altWidth={0.5}
              />
              <div className="AlignedRow" style={{ gap: 10 }}>
                {avatarInitial(
                  "",
                  "linear-gradient(0deg, rgb(89,168,255) 0%, rgb(158,107,255) 100%)",
                  18
                )}
                <LabelButton
                  text="coffee"
                  color={Colors.BLUE5}
                  backgroundColor={Colors.BLUE5 + "1a"}
                  borderColor={Colors.BLUE5}
                  containerStyles={{ fontSize: 12, fontWeight: 500 }}
                />
              </div>
            </div>
          </div>
          {isDesktopSize ? connector("AUTO-DM") : verticalConnector("AUTO-DM")}
          {/* Stage 2 */}
          <div
            className="ColumnNormal"
            style={{
              gap: 14,
              width: isDesktopSize ? 220 : "100%",
              maxWidth: isDesktopSize ? undefined : 332,
              alignItems: isDesktopSize ? "flex-start" : "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                ...styles.flowHeaderText,
                textAlign: isDesktopSize ? "left" : "center",
              }}
            >
              2 — LANDS IN THEIR INBOX
            </span>
            <div
              className="ColumnNormal"
              style={{ gap: 9, width: "100%", alignItems: "center" }}
            >
              <div className="AlignedRow" style={inboxRowStyle(false)}>
                {avatarInitial(
                  "M",
                  "linear-gradient(163deg, rgb(255,161,21) 36.85%, rgb(223,60,60) 139.99%)"
                )}
                <div
                  className="ColumnNormal"
                  style={{ gap: 2, flex: 1, minWidth: 0 }}
                >
                  <div className="AlignedRow">
                    <span
                      style={{
                        color: Colors.WHITE,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      Mom
                    </span>
                    <div style={{ flex: 1 }} />
                    <span style={{ color: "#73737a", fontSize: 9 }}>now</span>
                  </div>
                  <span style={{ color: "#73737a", fontSize: 10 }}>
                    Call me when you get a chance
                  </span>
                </div>
              </div>
              <div className="AlignedRow" style={inboxRowStyle(true)}>
                <img
                  src={ClaireSawaCarousel}
                  alt="Claire Sawa"
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <div
                  className="ColumnNormal"
                  style={{ gap: 2, flex: 1, minWidth: 0 }}
                >
                  <div className="AlignedRow">
                    <span
                      style={{
                        color: Colors.WHITE,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Claire Sawa
                    </span>
                    <div style={{ flex: 1 }} />
                    <span style={{ color: "#73737a", fontSize: 9 }}>now</span>
                  </div>
                  <span
                    style={{
                      color: "#dbe0ed",
                      fontSize: 10,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Hey Sarah! This is Claire! Here&apos;s the recipe...
                  </span>
                </div>
              </div>
              <div className="AlignedRow" style={inboxRowStyle(false)}>
                {avatarInitial(
                  "D",
                  "linear-gradient(225deg, rgb(29,70,255) 5.7%, rgb(17,184,255) 122.31%)"
                )}
                <div
                  className="ColumnNormal"
                  style={{ gap: 2, flex: 1, minWidth: 0 }}
                >
                  <div className="AlignedRow">
                    <span
                      style={{
                        color: Colors.WHITE,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      Dad
                    </span>
                    <div style={{ flex: 1 }} />
                    <span style={{ color: "#73737a", fontSize: 9 }}>
                      9:12AM
                    </span>
                  </div>
                  <span style={{ color: "#73737a", fontSize: 10 }}>
                    Happy birthday kiddo 🎉
                  </span>
                </div>
              </div>
            </div>
            <span
              style={{
                color: Colors.GRAY2,
                fontSize: 13,
                letterSpacing: 0.2,
                textAlign: isDesktopSize ? "left" : "center",
              }}
            >
              You&apos;re right next to Mom and Dad!
            </span>
          </div>
          {isDesktopSize ? connector("") : verticalConnector("")}
          {/* Stage 3 */}
          <div
            className="ColumnNormal"
            style={{
              gap: 14,
              width: isDesktopSize ? 260 : "100%",
              maxWidth: isDesktopSize ? undefined : 288,
              alignItems: isDesktopSize ? "flex-start" : "center",
              flexShrink: 0,
            }}
          >
            <span
              style={{
                ...styles.flowHeaderText,
                textAlign: isDesktopSize ? "left" : "center",
              }}
            >
              3 — YOUR TEXT LIST GROWS
            </span>
            <div className="ColumnNormal" style={{ gap: 8, width: "100%" }}>
              <div
                style={{
                  padding: 14,
                  borderRadius: 20,
                  backgroundColor: Colors.BLUE5,
                  boxShadow: "0 12px 17px rgba(0,122,255,0.28)",
                  width: "100%",
                }}
              >
                <span
                  style={{
                    color: Colors.WHITE,
                    fontSize: 13,
                    lineHeight: "19px",
                  }}
                >
                  Hey Sarah! This is Claire! Here&apos;s the recipe for the
                  coffee jelly! I&apos;ll send you other fun recipes and content
                  I publish 🤗
                </span>
              </div>
              <div
                className="ColumnNormal"
                style={{
                  gap: 8,
                  padding: 12,
                  borderRadius: 20,
                  backgroundColor: Colors.BLUE5,
                  boxShadow: "0 12px 17px rgba(0,122,255,0.28)",
                  width: "100%",
                }}
              >
                <img
                  src={IntegrateSocialsFlowRecipe}
                  alt="Coffee Jelly Recipe"
                  style={{
                    width: "100%",
                    height: 110,
                    borderRadius: 12,
                    objectFit: "cover",
                  }}
                />
                <div
                  className="ColumnNormal"
                  style={{ gap: 2, paddingInline: 2 }}
                >
                  <span
                    className="sectionTitle"
                    style={{ color: Colors.WHITE }}
                  >
                    Coffee Jelly Recipe
                  </span>
                  <span
                    style={{ color: "rgba(255,255,255,0.72)", fontSize: 13 }}
                  >
                    markitai.com · Recipe from Claire
                  </span>
                </div>
              </div>
              <div className="AlignedRow" style={{ alignSelf: "flex-end" }}>
                <span style={{ color: Colors.GRAY1, fontSize: 13 }}>
                  Read 9:41 AM
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(IntegrateSocialsFold);
